/**
 * RhMonDossierPanel — MON dossier agent, en lecture seule.
 *
 * ─── LE MANQUE QU'IL COMBLE ─────────────────────────────────────────────────
 *
 * « Mon espace agent » proposait « Mon dossier » et ouvrait les documents RH de
 * la commune. L'agent ne pouvait relire nulle part ses propres données — son
 * état civil, son grade, ses contrats, la personne à prévenir — alors que ce
 * sont les siennes, et que c'est précisément ce qu'on veut vérifier avant de
 * signaler une erreur aux RH.
 *
 * ─── POURQUOI LECTURE SEULE, ET POURQUOI C'EST LE BON CHOIX ─────────────────
 *
 * Le dossier agent est un acte de gestion : un changement de grade, de quotité
 * ou d'échelon découle d'un arrêté, pas d'une saisie de l'intéressé. Laisser
 * modifier ici créerait deux vérités pour la même donnée. L'agent constate et
 * signale ; la RH corrige.
 *
 * Ce que l'écran N'AFFICHE PAS, et volontairement :
 *  · le NIR — il ne sort jamais de la base, l'API n'en donne que le booléen ;
 *  · les observations de la RH — écrites pour la gestion, pas pour l'agent.
 */

import { useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { Card, Alert, Badge } from '@/components/ui';
import type { RhAgentDossier } from '@roronoa-collectivites/shared-types';
import {
  Loader2, AlertCircle, Info, UserRound, Briefcase, Phone, ShieldAlert,
  TrendingUp, FileSignature,
} from 'lucide-react';

interface Anciennetes {
  communeMois: number | null; fonctionPubliqueMois: number | null;
  dateEntreeCommune: string | null; dateEntreeFonctionPublique: string | null;
}
interface Avancement {
  id: string; grade: string | null; echelon: number | null;
  dateEffet: string; dateProchainAvancement: string | null;
  commentaire: string | null;
}
interface Contrat {
  id: string; typeContrat: string; dateDebut: string; dateFin: string | null;
  motif: string | null; dureeMois: number | null;
}

/** Europe/Paris, jamais toISOString() : une date d'anniversaire ne doit pas reculer d'un jour. */
function dateFr(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(new Date(`${iso.slice(0, 10)}T12:00:00Z`));
}

/** « 16 ans », « 7 mois » — l'ancienneté se lit, elle ne se compte pas. */
function dureeFr(mois: number | null): string {
  if (mois === null) return '—';
  const ans = Math.floor(mois / 12);
  const reste = mois % 12;
  if (ans === 0) return `${reste} mois`;
  if (reste === 0) return `${ans} an${ans > 1 ? 's' : ''}`;
  return `${ans} an${ans > 1 ? 's' : ''} et ${reste} mois`;
}

/** Une ligne « libellé / valeur ». Le tiret cadratin marque le champ non renseigné. */
function Ligne({ label, valeur }: { label: string; valeur: string | number | null | undefined }) {
  const v = valeur === null || valeur === undefined || valeur === '' ? '—' : String(valeur);
  return (
    <div>
      <dt className="text-2xs uppercase tracking-wide text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{v}</dd>
    </div>
  );
}

function Bloc({
  titre, icon: Icon, children,
}: {
  titre: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <Card variant="glass" padded>
      <h2 className="flex items-center gap-2 text-sm font-medium text-ink">
        <Icon className="h-4 w-4 text-rh" aria-hidden="true" />
        {titre}
      </h2>
      <div className="mt-3">{children}</div>
    </Card>
  );
}

export function RhMonDossierPanel() {
  const [agent, setAgent] = useState<RhAgentDossier | null>(null);
  const [anciennetes, setAnciennetes] = useState<Anciennetes | null>(null);
  const [avancements, setAvancements] = useState<Avancement[]>([]);
  const [contrats, setContrats] = useState<Contrat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.rh.monDossier();
        setAgent(r.agent);
        setAnciennetes(r.anciennetes);
        setAvancements(r.avancements);
        setContrats(r.contrats);
      } catch (e) {
        setError(e instanceof ApiException ? e.message : 'Dossier illisible');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <Card variant="glass" padded>
        <p className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
        </p>
      </Card>
    );
  }

  if (error) return <Alert variant="error" icon={AlertCircle}>{error}</Alert>;

  // Un maire, un adjoint ou un élu n'est pas nécessairement employé de la
  // commune : l'absence de dossier n'est pas une anomalie, et le dire ainsi
  // évite un ticket inutile.
  if (!agent) {
    return (
      <Alert variant="info" icon={Info}>
        Aucun dossier agent n'est rattaché à votre compte. C'est le cas des élus
        qui ne sont pas employés de la commune. Si vous pensez qu'il devrait y en
        avoir un, signalez-le au service des ressources humaines.
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <Alert variant="info" icon={Info}>
        Ces informations sont celles que la mairie détient sur vous, en lecture
        seule. Une donnée fausse ou périmée se corrige auprès du service des
        ressources humaines — un changement de grade ou de quotité découle d'un
        arrêté, pas d'une saisie.
      </Alert>

      <Bloc titre="État civil" icon={UserRound}>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Ligne label="Prénom" valeur={agent.prenom} />
          <Ligne label="Nom" valeur={agent.nom} />
          <Ligne label="Nom de naissance" valeur={agent.nomNaissance} />
          <Ligne label="Date de naissance" valeur={dateFr(agent.dateNaissance)} />
          <Ligne label="Lieu de naissance" valeur={agent.lieuNaissance} />
          <Ligne label="Matricule" valeur={agent.matricule} />
        </dl>
        {!agent.nirRenseigne && (
          <p className="mt-3 text-2xs text-ink-subtle">
            Votre numéro de sécurité sociale n'est pas renseigné. Il n'est jamais
            affiché ici, même lorsqu'il l'est.
          </p>
        )}
      </Bloc>

      <Bloc titre="Carrière" icon={Briefcase}>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Ligne label="Statut" valeur={agent.statut} />
          <Ligne label="Type de contrat" valeur={agent.typeContrat} />
          <Ligne label="Fin de contrat" valeur={agent.dateFinContrat ? dateFr(agent.dateFinContrat) : null} />
          <Ligne label="Cadre d'emploi" valeur={agent.cadreEmploi} />
          <Ligne label="Grade" valeur={agent.grade} />
          <Ligne label="Échelon" valeur={agent.echelon} />
          <Ligne label="Quotité de travail" valeur={agent.quotiteTravail !== null ? `${agent.quotiteTravail} %` : null} />
          <Ligne label="Fonction" valeur={agent.fonction} />
          <Ligne label="Entrée dans la commune" valeur={dateFr(agent.dateEntreeCommune)} />
          <Ligne label="Entrée fonction publique" valeur={dateFr(agent.dateEntreeFonctionPublique)} />
          {anciennetes && (
            <>
              <Ligne label="Ancienneté commune" valeur={dureeFr(anciennetes.communeMois)} />
              <Ligne label="Ancienneté fonction publique" valeur={dureeFr(anciennetes.fonctionPubliqueMois)} />
            </>
          )}
        </dl>
        <p className="mt-3 text-2xs text-ink-subtle">
          Les anciennetés sont calculées depuis vos dates d'entrée : elles ne sont
          jamais stockées, donc jamais périmées.
        </p>
      </Bloc>

      {avancements.length > 0 && (
        <Bloc titre="Grades et échelons" icon={TrendingUp}>
          <ul className="divide-y divide-border">
            {avancements.map((a) => (
              <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                <span className="text-sm text-ink">
                  {a.grade ?? 'Grade non précisé'}
                  {a.echelon !== null && <span className="text-ink-muted"> · échelon {a.echelon}</span>}
                </span>
                <span className="text-2xs text-ink-subtle">
                  effet au {dateFr(a.dateEffet)}
                  {a.dateProchainAvancement && ` · prochain le ${dateFr(a.dateProchainAvancement)}`}
                </span>
              </li>
            ))}
          </ul>
        </Bloc>
      )}

      {contrats.length > 0 && (
        <Bloc titre="Contrats" icon={FileSignature}>
          <ul className="divide-y divide-border">
            {contrats.map((c) => (
              <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2">
                <span className="text-sm text-ink">
                  <Badge variant="neutral" size="sm">{c.typeContrat.toUpperCase()}</Badge>
                  {c.motif && <span className="ml-2 text-ink-muted">{c.motif}</span>}
                </span>
                <span className="text-2xs text-ink-subtle">
                  du {dateFr(c.dateDebut)}
                  {c.dateFin ? ` au ${dateFr(c.dateFin)}` : ' (sans terme)'}
                  {c.dureeMois !== null && ` · ${c.dureeMois} mois`}
                </span>
              </li>
            ))}
          </ul>
        </Bloc>
      )}

      <Bloc titre="Mes coordonnées" icon={Phone}>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Ligne label="E-mail du compte" valeur={agent.emailCompte} />
          <Ligne label="E-mail personnel" valeur={agent.emailPerso} />
          <Ligne label="Téléphone personnel" valeur={agent.telephonePerso} />
          <Ligne label="Adresse" valeur={agent.adresse} />
          <Ligne label="Code postal" valeur={agent.codePostal} />
          <Ligne label="Ville" valeur={agent.ville} />
        </dl>
      </Bloc>

      <Bloc titre="Personne à prévenir" icon={ShieldAlert}>
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Ligne label="Nom" valeur={agent.urgenceNom} />
          <Ligne label="Téléphone" valeur={agent.urgenceTel} />
          <Ligne label="Lien" valeur={agent.urgenceLien} />
        </dl>
        <p className="mt-3 text-2xs text-ink-subtle">
          C'est la personne appelée en cas d'accident de service. Si elle a
          changé, prévenez les ressources humaines : c'est le seul champ du
          dossier qu'un oubli rend inutile au moment où il servirait.
        </p>
      </Bloc>
    </div>
  );
}
